import type { ReactElement } from 'react'
import type { PostEmbed } from '@/types/posts'
import { isSafeUrl } from '@/lib/url'
import { ImageGrid } from '@/components/ImageGrid'
import { VideoPlayer } from '@/components/VideoPlayer'
import { MediaWarningOverlay } from '@/components/ContentWarning'
import { StandardSiteReadInAppCTA } from '@/components/StandardSiteReadInAppCTA'
import { StandardSiteArticleCard } from '@/components/StandardSiteArticleCard'
import { YouTubeEmbed } from '@/components/YouTubeEmbed'
import { SpotifyEmbed } from '@/components/SpotifyEmbed'
import { TwitterEmbed } from '@/components/TwitterEmbed'
import { DivineEmbed } from '@/components/DivineEmbed'
import { FediverseEmbed } from '@/components/FediverseEmbed'
import {
    isGifUrl,
    extractYouTubeVideoId,
    extractSpotifyInfo,
    extractTweetId,
    extractDivineVideoEventId,
    isLikelyFediversePostUrl,
} from '@/lib/embed'
import { parseStandardDocumentUri } from '@/lib/standardSite'

export interface PostEmbedDisplayProps {
    embed?: PostEmbed | null
    contentWarning?: { text: string; scope: 'content' | 'media' } | null
    /** When true, external embeds are hidden (used for truncated bridged posts) */
    isTruncated?: boolean
    /** When true, a fediverse-shaped external URL is rendered as a plain link card instead of resolving into a quote-post card */
    excludeFediverse?: boolean
    /** stacked = full-width (detail/reply views); default = 2-col grid (feed) */
    imageLayout?: 'default' | 'stacked'
}

export function PostEmbedDisplay({
    embed,
    contentWarning,
    isTruncated = false,
    excludeFediverse = false,
    imageLayout = 'default',
}: PostEmbedDisplayProps): ReactElement | null {
    if (!embed) return null
    if (isTruncated && embed.type === 'external') return null

    const content = renderEmbedContent(embed, imageLayout, excludeFediverse)
    if (!content) return null

    if (contentWarning?.scope === 'media') {
        return <MediaWarningOverlay text={contentWarning.text}>{content}</MediaWarningOverlay>
    }

    return content
}

function renderEmbedContent(
    embed: PostEmbed,
    imageLayout: 'default' | 'stacked',
    excludeFediverse: boolean
): ReactElement | null {
    if (embed.type === 'images' && embed.images?.length) {
        return (
            <ImageGrid
                images={embed.images}
                layout={imageLayout === 'stacked' ? 'stacked' : undefined}
            />
        )
    }

    if (embed.type === 'video' && embed.video) {
        return (
            <VideoPlayer
                playlist={embed.video.playlist}
                thumbnail={embed.video.thumbnail}
                aspectRatio={embed.video.aspectRatio}
                alt={embed.video.alt}
            />
        )
    }

    if (embed.type === 'external' && embed.external) {
        if (isGifUrl(embed.external.uri) && isSafeUrl(embed.external.uri)) {
            return (
                <a
                    href={embed.external.uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-lg overflow-hidden border border-border-light dark:border-border-dark"
                    onClick={e => e.stopPropagation()}
                >
                    <img
                        src={embed.external.uri}
                        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty description/title should fall through to the next candidate, not render blank
                        alt={embed.external.description || embed.external.title || 'GIF'}
                        className="w-full min-h-[200px] max-h-[300px] object-contain bg-black/5 dark:bg-white/5"
                        loading="lazy"
                        onError={e => {
                            e.currentTarget.style.display = 'none'
                        }}
                    />
                </a>
            )
        }

        const youtubeId = extractYouTubeVideoId(embed.external.uri)
        if (youtubeId) {
            return <YouTubeEmbed videoId={youtubeId} title={embed.external.title} />
        }

        const spotifyInfo = extractSpotifyInfo(embed.external.uri)
        if (spotifyInfo) {
            return (
                <SpotifyEmbed
                    type={spotifyInfo.type}
                    id={spotifyInfo.id}
                    title={embed.external.title}
                />
            )
        }

        const tweetId = extractTweetId(embed.external.uri)
        if (tweetId) {
            return <TwitterEmbed tweetId={tweetId} title={embed.external.title} />
        }

        const divineEventId = extractDivineVideoEventId(embed.external.uri)
        if (divineEventId) {
            return <DivineEmbed eventId={divineEventId} title={embed.external.title} />
        }

        if (parseStandardDocumentUri(embed.associatedRefs)) {
            return <StandardSiteArticleCard embed={embed} />
        }

        // Generic link-card fallback (also used as the fediverse loading/error fallback)
        const genericLinkCard = (
            <>
                <a
                    href={isSafeUrl(embed.external.uri) ? embed.external.uri : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block border border-border-light dark:border-border-dark rounded-lg overflow-hidden hover:bg-border-light/10 dark:hover:bg-border-dark/10 transition-colors"
                    onClick={e => e.stopPropagation()}
                >
                    {embed.external.thumb && (
                        <img
                            src={embed.external.thumb}
                            alt={embed.external.title ?? ''}
                            className="w-full h-32 object-cover"
                            loading="lazy"
                            onError={e => {
                                e.currentTarget.style.display = 'none'
                            }}
                        />
                    )}
                    <div className="p-3">
                        {embed.external.title && (
                            <p className="text-sm font-semibold text-text-primary-light dark:text-text-primary-dark line-clamp-1">
                                {embed.external.title}
                            </p>
                        )}
                        {embed.external.description && (
                            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark line-clamp-2 mt-0.5">
                                {embed.external.description}
                            </p>
                        )}
                        <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-1 truncate">
                            {(() => {
                                try {
                                    return new URL(embed.external.uri).hostname
                                } catch {
                                    return embed.external.uri
                                }
                            })()}
                        </p>
                    </div>
                </a>
                <StandardSiteReadInAppCTA embed={embed} />
            </>
        )

        if (
            !excludeFediverse &&
            isLikelyFediversePostUrl(embed.external.uri, embed.external.title)
        ) {
            return <FediverseEmbed uri={embed.external.uri} fallback={genericLinkCard} />
        }

        return genericLinkCard
    }

    return null
}
